import { supabase } from './supabase';

interface UploadResponse {
  success: boolean;
  videoUrl?: string;
  error?: string;
}

export const uploadVideoToWasabi = async (
  file: File,
  orderId: string
): Promise<UploadResponse> => {
  const log = (...args: any[]) => console.log('[videoUpload]', ...args);

  try {
    log('start', { name: file.name, size: file.size, type: file.type });

    const validExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const isVideo = file.type.startsWith('video/') || validExtensions.includes(fileExtension);

    if (!isVideo) {
      return { success: false, error: 'Please select a video file (MP4, MOV, WEBM, etc.)' };
    }

    if (file.size > 1000 * 1024 * 1024) {
      return { success: false, error: 'Video must be less than 1GB' };
    }

    log('requesting presigned url...');
    const { data: presignData, error: presignError } = await supabase.functions.invoke('wasabi-presign', {
      body: {
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        uploadType: 'video',
        orderId,
      },
    });

    log('presign response', { hasUrl: !!presignData?.presignedUrl, presignError });

    if (presignError || !presignData?.presignedUrl) {
      console.error('[videoUpload] Presign error:', presignError || presignData?.error);
      return { success: false, error: presignData?.error || presignError?.message || 'Failed to get upload URL' };
    }

    // Guard against an indefinite hang on the direct Wasabi PUT (e.g. CORS stall).
    const controller = new AbortController();
    const timeoutMs = 5 * 60 * 1000; // 5 minutes
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    log('PUT to wasabi...', presignData.presignedUrl.split('?')[0]);
    let uploadResponse: Response;
    try {
      uploadResponse = await fetch(presignData.presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'video/mp4' },
        body: file,
        signal: controller.signal,
      });
    } catch (putErr: any) {
      clearTimeout(timer);
      console.error('[videoUpload] PUT fetch threw:', putErr);
      if (putErr?.name === 'AbortError') {
        return { success: false, error: 'Upload timed out. Please try again or use a smaller file.' };
      }
      // A CORS preflight failure in the browser usually surfaces as a generic TypeError ("Failed to fetch").
      return { success: false, error: 'Network/CORS error uploading to storage. Please refresh and try again.' };
    }
    clearTimeout(timer);

    log('PUT status', uploadResponse.status);

    if (!uploadResponse.ok) {
      const errText = await uploadResponse.text().catch(() => '');
      console.error('[videoUpload] Wasabi rejected upload:', uploadResponse.status, errText);
      return { success: false, error: `Upload failed (${uploadResponse.status})` };
    }

    log('upload ok', presignData.publicUrl);
    return { success: true, videoUrl: presignData.publicUrl };
  } catch (error: any) {
    console.error('[videoUpload] unexpected error:', error);
    return { success: false, error: error?.message || 'Upload failed' };
  }
};
